/* eslint-disable react/no-unescaped-entities */
import React, { useState, useEffect, useRef } from "react";
import { CloseIcon } from "../assets/CloseIcon";
import { ThumbsDownBlackIcon } from "../assets/ThumbsDownBlackIcon";
import { ThumbsDownRedIcon } from "../assets/ThumbsDownRedIcon";
import { ThumbsUpBlackIcon } from "../assets/ThumbsUpBlackIcon";
import { ThumbsUpGreenIcon } from "../assets/ThumbsUpGreenIcon";
import CustomAlert from "../components/atoms/CustomAlert";
import SearchHeader from "../components/atoms/SearchHeader";
import RelatedQuestions from "../components/molecules/RelatedQuestions";
import SearchBar from "../components/molecules/SearchBar";
import ResponseContent from "../components/organisms/ResponseContent";
import UserFeedback from "../components/organisms/UserFeedback";
import { useSearch } from "../context/SearchContext";
import { Source } from "../services/PipelineService";
import {
  sendSourceLinkClick,
  sendUserFeedbackSelected,
} from "../utils/analyticsEvents";

const FeedbackSection: React.FC<{
  isFeedbackSent: boolean;
  handleFeedbackClick: (feedback: "negative" | "positive") => void;
  isMobile?: boolean;
  suggestionsEnabled?: boolean;
  isTextResponseComplete?: boolean;
}> = ({
  isFeedbackSent,
  handleFeedbackClick,
  isMobile = false,
  suggestionsEnabled = false,
  isTextResponseComplete = false,
}) => {
  if (isFeedbackSent || !isTextResponseComplete) {
    return null;
  }

  return (
    <div
      className={`flex flex-col pt-4 mt-8 items-start ${
        suggestionsEnabled ? "blur-sm" : ""
      }`}
    >
      <h3
        className={`text-xl text-BCP-dark-blue mb-4 font-flexo-demi ${
          isMobile ? "text-center" : "text-left"
        }`}
      >
        ¿Resolvimos tu consulta?
      </h3>
      <div
        className={`flex space-x-3 ${
          isMobile ? "justify-center" : "justify-start"
        }`}
      >
        <button
          className="rounded-full py-2 px-4 bg-[#F6F6F7] hover:bg-BCP-light-green flex font-semibold group"
          onClick={() => handleFeedbackClick("positive")}
        >
          <ThumbsUpBlackIcon className="w-6 h-6 mr-2 group-hover:hidden" />
          <ThumbsUpGreenIcon className="w-6 h-6 mr-2 hidden group-hover:block" />
          <p className="text-[#264B78] group-hover:text-[#37A500]">Sí</p>
        </button>
        <button
          className="rounded-full p-2 px-4 bg-[#F6F6F7] hover:bg-[#FFF2F5] flex font-semibold group"
          onClick={() => handleFeedbackClick("negative")}
        >
          <ThumbsDownBlackIcon className="w-6 h-6 mr-2 group-hover:hidden" />
          <ThumbsDownRedIcon className="w-6 h-6 mr-2 hidden group-hover:block" />
          <p className="text-[#264B78] group-hover:text-BCP-error">No</p>
        </button>
      </div>
    </div>
  );
};

const carouselStyles = `
  .hide-scrollbar {
    -ms-overflow-style: none;
    scrollbar-width: none;
  }
  .hide-scrollbar::-webkit-scrollbar {
    display: none;
  }
`;

const ResponsePage: React.FC = () => {
  const {
    handleSearch,
    currentSearchTerm,
    textResponse,
    isLoading,
    isTextResponseComplete,
    sources,
    banners,
    actions,
    tutorials,
    relatedQueries,
    requestId,
    contextId,
    resetSearch,
    hasError,
  } = useSearch();

  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
  const [suggestionsEnabled, setSuggestionsEnabled] = useState(false);
  const [selectedFeedback, setSelectedFeedback] = useState<
    "negative" | "positive" | null
  >(null);
  const [isFeedbackSent, setIsFeedbackSent] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const asideRef = useRef<HTMLDivElement>(null);
  const [headerHeight, setHeaderHeight] = useState(0);

  useEffect(() => {
    if (requestId) {
      setIsFeedbackSent(false);
      setSelectedFeedback(null);
      setShowAlert(false);
    }
  }, [requestId]);

  useEffect(() => {
    if (isFeedbackSent) {
      setShowAlert(true);
      const timer = setTimeout(() => {
        setShowAlert(false);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [isFeedbackSent]);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 100);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const updateHeaderHeight = () => {
      const headerElement = document.getElementById("mobile-header");
      if (headerElement) {
        const height = headerElement.offsetHeight;
        setHeaderHeight(height);
      }
    };
    updateHeaderHeight();
    window.addEventListener("resize", updateHeaderHeight);
    if (currentSearchTerm) {
      setTimeout(updateHeaderHeight, 100);
    }

    return () => {
      window.removeEventListener("resize", updateHeaderHeight);
    };
  }, [currentSearchTerm]);

  const handleFeedbackClick = (feedback: "negative" | "positive") => {
    sendUserFeedbackSelected(requestId, currentSearchTerm, feedback, contextId);
    setSelectedFeedback(feedback);
    setIsFeedbackOpen(true);
  };

  const handleFeedbackClose = () => {
    setIsFeedbackOpen(false);
  };

  const removeSpecificParams = (url: URL, setRfidValue: string) => {
    const paramsToRemove = [
      "utm_source",
      "utm_medium",
      "utm_campaign",
      "utm_content",
      "pcid",
      "rfid",
    ];
    paramsToRemove.forEach((param) => {
      url.searchParams.delete(param);
    });
    url.searchParams.set("rfid", setRfidValue);
    url.search = url.search.replace(/%3A/g, ":");
    return url.toString();
  };

  const MoreInformationSection: React.FC = () => {
    const uniqueSources = sources.filter(
      (source, index, self) =>
        index === self.findIndex((e) => e.url === source.url),
    );

    const handleSourceClick = (source: Source) => {
      sendSourceLinkClick(
        currentSearchTerm,
        requestId,
        source.label || "",
        source.url,
        source.title,
        contextId,
      );
    };

    return (
      <>
        {uniqueSources.length > 0 && (
          <div className="mt-8">
            <h2 className="text-xl font-flexo-demi mb-4">Más información en</h2>
            <div className="md:grid md:gap-4 md:grid-cols-2 flex flex-nowrap overflow-x-auto hide-scrollbar snap-x snap-mandatory pb-2">
              {uniqueSources
                .slice(0, 5)
                .map((source: Source, index: number) => {
                  source.url = removeSpecificParams(
                    new URL(source.url),
                    "buscador:fuente:pilotoIA",
                  );

                  return (
                    <div
                      key={index}
                      className="bg-white border rounded-xl p-3 shadow hover:shadow-lg transition-shadow flex-shrink-0 md:w-auto md:mr-4 md:h-auto snap-start w-[217px] h-[116px] mr-3 flex flex-col justify-between overflow-hidden"
                    >
                      <a
                        href={source.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="h-full flex flex-col justify-between"
                        onClick={() => handleSourceClick(source)}
                      >
                        {source.label && (
                          <div className="rounded px-2 py-1 bg-BCP-blue-selected-text mb-1 w-fit">
                            <p className="text-sm text-white truncate">
                              {source.label}
                            </p>
                          </div>
                        )}

                        <h3 className="text-sm text-BCP-normal-text font-flexo-demi line-clamp-3">
                          {source.title}
                        </h3>
                      </a>
                    </div>
                  );
                })}
            </div>
          </div>
        )}
        {showAlert && (
          <div className="w-full flex mt-6 md:block hidden">
            <CustomAlert
              message="¡Gracias por ayudarnos a construir una mejor experiencia!"
              visible={showAlert}
              onClose={() => setShowAlert(false)}
            />
          </div>
        )}
      </>
    );
  };

  return (
    <div className="flex flex-col w-full min-h-screen items-center">
      <style>{carouselStyles}</style>

      <div
        className="md:hidden fixed top-0 left-0 right-0 bg-white z-50 px-6"
        id="mobile-header"
      >
        <div className="flex justify-between items-center mb-2">
          <span></span>
          <button onClick={() => resetSearch()}>
            <CloseIcon className="w-8 h-8" />
          </button>
        </div>
        <div>
          <div className="text-BCP-dark-gray text-[14px] font-[400]">
            Resultados de búsqueda para
          </div>
          <div className="text-BCP-dark-blue text-xl font-flexo-demi font-bold mb-4">
            "{currentSearchTerm}"
          </div>
        </div>
      </div>

      <div className="xl:px-0 md:px-0 2xs:px-6 xs:px-6 sm:px-6 md:flex w-full max-w-[1200px] gap-2 md:mt-16 md:mb-16 mb-44 md:pt-0 pt-0">
        <div
          className="w-full md:w-1/2 flex flex-col md:mt-0"
          style={{
            marginTop: `${headerHeight - 30}px `,
          }}
        >
          <div className="md:block hidden">
            <div className="text-BCP-dark-gray text-[16px] font-[400]">
              Resultados de búsqueda para
            </div>
            <SearchHeader>"{currentSearchTerm}"</SearchHeader>
          </div>

          <div className="md:max-w-[600px] mt-2 md:mt-8">
            <SearchBar
              queryDisplayMode="responsePage"
              isInternalSearch={true}
              onMoreQuestionsClick={() => {}}
              onSuggestionsEnabledChange={(enabled) =>
                setSuggestionsEnabled(enabled)
              }
              isLoading={isLoading}
              onProductosPreaprobadosClick={() => {}}
              onSearchSubmit={() => {
                window.scrollTo({ top: 0, behavior: "smooth" });
              }}
            />
          </div>

          {hasError && isLoading ? (
            <div className="flex flex-col items-center justify-center py-8">
              <div className="text-BCP-dark-blue text-md font-flexo-demi mb-2">
                Algo salió mal, estamos volviendo a intentarlo...
              </div>
              <div className="flex justify-center items-center max-w-[4rem] mt-4">
                <img src="simpleSpinner.gif" alt="loading" />
              </div>
            </div>
          ) : (
            <>
              <ResponseContent
                textResponse={textResponse}
                banners={banners}
                actions={actions}
                tutorials={tutorials}
                query={currentSearchTerm}
                requestId={requestId}
                contextId={contextId}
                suggestionsEnabled={suggestionsEnabled}
              />

              {isLoading && textResponse.length === 0 && (
                <div className="flex justify-center items-center max-w-[4rem] mt-8 md:my-12 mx-auto">
                  <img src="simpleSpinner.gif" alt="loading" />
                </div>
              )}

              <div className="md:hidden block">
                <FeedbackSection
                  isFeedbackSent={isFeedbackSent}
                  handleFeedbackClick={handleFeedbackClick}
                  isMobile={true}
                  suggestionsEnabled={suggestionsEnabled}
                  isTextResponseComplete={isTextResponseComplete}
                />
              </div>
            </>
          )}
        </div>
        {hasError && isLoading ? (
          <></>
        ) : (
          <aside
            ref={asideRef}
            className={`
            ${suggestionsEnabled ? "blur-sm" : ""}
            md:block md:w-1/2 text-BCP-dark-blue md:ml-10 md:mt-6
            ${
              isScrolled
                ? "md:sticky md:top-6 md:self-start md:max-h-[90vh] md:overflow-y-auto"
                : ""
            }
          `}
          >
            <button
              className="md:flex ml-auto mr-0 hidden"
              onClick={() => resetSearch()}
            >
              <CloseIcon className="w-8 h-8" />
            </button>
            <div className="mt-16">
              <RelatedQuestions
                relatedQuestions={relatedQueries}
                onQuestionClick={(question) =>
                  handleSearch(question, "internal", "related")
                }
              />
            </div>
            <MoreInformationSection />
            <div className="hidden md:block">
              <FeedbackSection
                isFeedbackSent={isFeedbackSent}
                handleFeedbackClick={handleFeedbackClick}
                isMobile={false}
                suggestionsEnabled={suggestionsEnabled}
                isTextResponseComplete={isTextResponseComplete}
              />
            </div>
            {!isLoading && showAlert && (
              <div className="w-full flex justify-start mt-6 md:hidden block">
                <CustomAlert
                  message="¡Gracias por ayudarnos a construir una mejor experiencia!"
                  visible={showAlert}
                  onClose={() => setShowAlert(false)}
                />
              </div>
            )}
          </aside>
        )}
      </div>

      <UserFeedback
        requestId={requestId}
        contextId={contextId}
        query={currentSearchTerm}
        isOpen={isFeedbackOpen}
        onClose={handleFeedbackClose}
        selectedFeedback={selectedFeedback}
        setSelectedFeedback={setSelectedFeedback}
        onFeedbackChange={(feedback) => setSelectedFeedback(feedback)}
        isFeedbackSent={isFeedbackSent}
        setIsFeedbackSent={setIsFeedbackSent}
      />
    </div>
  );
};

export default ResponsePage;
